import { ref } from 'vue';

import type { MatchingTaxonomy, RoutingOptions, WoodyConfig } from '@/types/mode-list-config';
import type { Filters, TranslatedField } from '@/types/mode-list-filters';
import type { PriceRangeMetric, SessionDurationMetric, TimeRangeMetric } from '@/types/mode-list-metrics.js';
import type { Product, ProductDTO } from '@/types/mode-list-products';

import useLocale from './useLocale';
import useModeListFilters from './useModeListFilters';

const woodyConfig = ref<WoodyConfig | null>(null);
const woodyTaxonomies = ref<MatchingTaxonomy>();
const apiKey = ref<string>();
const apiURL = ref('');
const assetsURL = ref('');

const products = ref<Array<Product>>([]);
const totalResults = ref(0);
const nextPaginationKey = ref('');

const themesRouting = ref<Array<RoutingOptions>>([]);
const canonicals = ref<any>({});
const priceRange = ref<PriceRangeMetric>({ min: 0, max: 1500, count: 10 });
const sessionDuration = ref<SessionDurationMetric>({ min: 0, max: 24 });
const timeRanges = ref<Array<TimeRangeMetric>>([]);
const themes = ref<Array<{ id: string; name: string; count: number; type: 'THEMES'; translatedName: Array<TranslatedField> }>>([]);
const types = ref<Array<{ id: string; name: string; count: number; type: 'TYPES'; isSelected: boolean; isMatching: boolean; logo: string; translatedName: Array<TranslatedField> }>>([]);
const places = ref<Array<{ id: string; name: string; count: number; type: 'PLACES'; translatedName: Array<TranslatedField> }>>([]);
const audiences = ref<Array<{ id: string; name: string; count: number; type: 'AUDIENCES'; translatedName: Array<TranslatedField> }>>([]);
const internals = ref<Array<{ id: string; name: string; count: number; type: 'INTERNALS'; translatedName: Array<TranslatedField> }>>([]);
const baseMetrics = ref<Array<any>>([]);

function setupAPIEnvironnement() {
  const isPreprod = window.location.origin.includes('localhost') || window.location.origin.includes('.rc-preprod.com') || window.location.origin.includes('preprod.marseille-tourisme.com');
  !isPreprod ? apiURL.value = 'https://public.api.youbook.travel/v3' : apiURL.value = 'https://public.api.youbook.rc-preprod.com/v3';
  !isPreprod ? assetsURL.value = 'https://components.youbook.travel/activities_svg' : assetsURL.value = 'https://components.youbook.rc-preprod.com/activities_svg';
}

setupAPIEnvironnement();

export default function useModeListData() {
  async function setupWoodyConfig(config: WoodyConfig) {
    woodyConfig.value = config;
    apiKey.value = config.apikey;

    if (!woodyConfig.value.options) {
      woodyConfig.value.options = { hasModeMap: false, hasCalendar: true, hasDetailView: true, hasFilters: { price: true } };
    }
    else {
      woodyConfig.value.options.hasModeMap ??= false;
      woodyConfig.value.options.hasCalendar ??= true;
      woodyConfig.value.options.hasDetailView ??= true;

      if (!woodyConfig.value.options.hasFilters) {
        woodyConfig.value.options = {
          ...woodyConfig.value.options,
          hasFilters: { price: true },
        };
      }
      else {
        woodyConfig.value.options.hasFilters.price ??= true;
      }
    }

    const { filters, saveFiltersToLocalStorage } = useModeListFilters();
    const { currentLocale } = useLocale();

    if (woodyConfig.value?.homeUrl && window.location.href === woodyConfig.value.homeUrl) {
      filters.value.selections = [];
      filters.value.metrics = filters.value.metrics.filter((it: any) => it.type !== 'crossedTaxonomyCount');
      filters.value.sorts = [{ type: 'random', seed: Date.now() }];
      const customFilterImported = filters.value.filters.find(it => it.filterProperties.type === 'custom_filter' && it.filterProperties.target === 'product' && it.filterProperties.field === 'imported_in_langs');
      if (!customFilterImported) {
        filters.value.filters.push({
          filterProperties: {
            type: 'custom_filter',
            target: 'product',
            field: 'imported_in_langs',
          },
          selection: currentLocale.value,
        });
      }
      saveFiltersToLocalStorage();
    }

    if (woodyConfig.value?.routingEndpoint) {
      try {
        const routingEndpointFetch = await fetch(`${woodyConfig.value.routingEndpoint}`, { method: 'GET' });
        const routingEndpointData = await routingEndpointFetch.json();
        themesRouting.value = routingEndpointData;
      }
      catch (err) {
        console.error('No routing data retrieved', err);
      }
    }

    if (woodyConfig.value?.canonicalsEndpoint) {
      const canonicalsEndpoint = woodyConfig.value?.canonicalsEndpoint ?? `${window.location.origin}/wp-json/woody/youbook/canonicals?lang=${currentLocale.value.substring(0, 2)}`;
      try {
        const canonicalEndpointFetch = await fetch(`${canonicalsEndpoint}`, { method: 'GET' });
        canonicals.value = await canonicalEndpointFetch.json();
      }
      catch (err) {
        console.error('No canonicals data retrieved', err);
      }
    }
  }

  function setApiKey(newApiKey: string) {
    apiKey.value = newApiKey;
  }

  function setupTaxonomies(taxonomies: MatchingTaxonomy) {
    if (!taxonomies || taxonomies.length === 0)
      return;
    woodyTaxonomies.value = taxonomies;
    const { filters, loadFiltersFromLocalStorage, saveFiltersToLocalStorage } = useModeListFilters();

    loadFiltersFromLocalStorage();
    filters.value.metrics = filters.value.metrics.filter((it: any) => it.type !== 'crossedTaxonomyCount');
    filters.value.selections = [];

    for (const woodyTaxonomy of woodyTaxonomies.value) {
      switch (woodyTaxonomy.type) {
        case 'THEMES':
          filters.value.metrics.push({ type: 'crossedTaxonomyCount', taxonomyTypes: ['TYPES'], crossedTaxonomies: { themes: [...woodyTaxonomy.value] } });
          filters.value.selections.push({ filterProperties: { type: 'taxonomyThemes' }, selection: [...woodyTaxonomy.value] });
          break;
        case 'TYPES':
          filters.value.selections.push({ filterProperties: { type: 'taxonomyTypes' }, selection: [...woodyTaxonomy.value] });
          break;
        case 'AUDIENCES':
          filters.value.selections.push({ filterProperties: { type: 'taxonomyAudiences' }, selection: [...woodyTaxonomy.value] });
          break;
        case 'PLACES':
          filters.value.selections.push({ filterProperties: { type: 'taxonomyPlaces' }, selection: [...woodyTaxonomy.value] });
          break;
        case 'INTERNALS':
          filters.value.selections.push({ filterProperties: { type: 'taxonomyInternals' }, selection: [...woodyTaxonomy.value] });
          break;
        default:
          console.error('Invalid taxonomy found in config');
      }
    }
    saveFiltersToLocalStorage();
  }

  async function fetchInitialProductsList(filters: Filters) {
    try {
      const productDTOs = await getProductsList(filters);
      products.value = formatToProducts(productDTOs.data);
      totalResults.value = productDTOs.total;
      nextPaginationKey.value = productDTOs.nextPage;

      computeMetrics(productDTOs);
    }
    catch (err) {
      console.error('Unable to fetch data', err);
    }
  }

  async function getProductsList(filters: Filters, signal?: AbortSignal) {
    if (apiKey.value == null) {
      throw new Error('No client token available');
    }
    const path = '/products/list';
    const requestFilters = { ...filters };
    const customFilterImported = requestFilters.filters.find(it => it.filterProperties.type === 'custom_filter' && it.filterProperties.target === 'product' && it.filterProperties.field === 'imported_in_langs');
    if (!customFilterImported) {
      const { currentLocale } = useLocale();
      requestFilters.filters.push({
        filterProperties: {
          type: 'custom_filter',
          target: 'product',
          field: 'imported_in_langs',
        },
        selection: currentLocale.value,
      });
    }
    const body = JSON.stringify(requestFilters);
    const headers = new Headers();
    headers.set('Content-Type', 'application/json');
    headers.set('Accept', 'application/json');
    headers.set('Authorization', `Bearer ${apiKey.value}`);
    const request = await fetch(`${apiURL.value}${path}`, { method: 'POST', body, headers, signal });
    return await request.json();
  }

  async function fetchProductsList(filters: Filters, signal?: AbortSignal) {
    const requestFilters = { ...filters };
    try {
      const productDTOs = await getProductsList(requestFilters, signal);
      products.value = formatToProducts(productDTOs.data);
      totalResults.value = productDTOs.total;
      nextPaginationKey.value = productDTOs.nextPage;
      computeMetrics(productDTOs);
    }
    catch (err) {
      if (signal?.aborted) {
        throw err;
      }
      console.error('Unable to fetch data', err);
      return [];
    }
  }

  async function additionalFetchProductsList(filters: Filters) {
    const requestFilters = { ...filters };
    requestFilters.size = 0;
    try {
      const productDTOs = await getProductsList(requestFilters);
      baseMetrics.value = productDTOs.metrics;
    }
    catch (err) {
      console.error('Unable to fetch data', err);
    }
  }

  async function getBaseMetrics(filters: Filters) {
    const requestFilters = { ...filters };
    requestFilters.size = 0;
    try {
      const productDTOs = await getProductsList(requestFilters);
      return productDTOs.metrics;
    }
    catch (err) {
      console.error('Unable to fetch data', err);
    }
  }

  async function computeMetrics(productDTOs: any) {
    const priceFromMetrics = productDTOs?.metrics?.find((it: any) => it.metricName === 'priceFromWithSelection');
    if (priceFromMetrics != null) {
      priceRange.value = priceFromMetrics.value;
    }
    const sessionDurationMetrics = productDTOs?.metrics?.find((it: any) => it.metricName === 'sessionDuration');
    if (sessionDurationMetrics != null) {
      sessionDuration.value = sessionDurationMetrics.value;
    }
    // const timeRangeMetrics = productDTOs?.metrics?.find((it: any) => it.metricName === 'timeRange');
    const timeRangeMetrics = productDTOs?.metrics?.find((it: any) => it.metricName === 'timeRangeWithSelection');
    if (timeRangeMetrics != null) {
      timeRanges.value = timeRangeMetrics.value;
    }
    const themeMetrics = productDTOs?.metrics?.find((it: any) => it.metricName === 'taxonomyThemes');
    if (themeMetrics != null) {
      themes.value = themeMetrics.value;
    }
    const audiencesMetrics = productDTOs?.metrics?.find((it: any) => it.metricName === 'taxonomyAudiences');
    if (audiencesMetrics != null) {
      audiences.value = audiencesMetrics.value;
    }
    const internalsMetrics = productDTOs?.metrics?.find((it: any) => it.metricName === 'taxonomyInternals');
    if (internalsMetrics != null) {
      internals.value = internalsMetrics.value;
    }
    const placesMetrics = productDTOs?.metrics?.find((it: any) => it.metricName === 'taxonomyPlaces');
    if (placesMetrics != null) {
      places.value = placesMetrics.value;
    }
    // todo : A améliorer - fix rapide pour débloquer l'éditeur de widgets
    const placesWithSelectionMetrics = productDTOs?.metrics?.find((it: any) => it.metricName === 'taxonomyPlacesWithSelection');
    if (placesWithSelectionMetrics != null) {
      places.value = placesWithSelectionMetrics.value;
    }

    const crossedMetrics = productDTOs?.metrics?.find((it: any) => it.metricName === 'crossedTaxonomyCount');
    const typesMetrics = productDTOs?.metrics?.find((it: any) => it.metricName === 'taxonomyTypes');
    const crossedTypes = crossedMetrics?.value.types ?? [];
    if (typesMetrics != null) {
      const { filters } = useModeListFilters();
      const currentSelection = filters.value.selections.find((it: any) => it.filterProperties?.type === 'taxonomyTypes')?.selection ?? [];
      if (crossedTypes.length) {
        types.value = [
          ...typesMetrics.value.map((it: any) => {
            const matchingType = crossedTypes.find((x: any) => x.id === it.id);
            if (matchingType) {
              return { ...matchingType, isMatching: true, isSelected: currentSelection.includes(it.id) };
            }
            else {
              return { ...it, isMatching: false, isSelected: currentSelection.includes(it.id) };
            }
          }),
        ];
      }
      else {
        types.value = typesMetrics.value.map((it: any) => {
          return { ...it, isMatching: true, isSelected: currentSelection.includes(it.id) };
        });
      }
    }

    const { filters } = useModeListFilters();
    const searchPresent = filters.value.sorts.find((it: any) => it.type === 'search');
    if (searchPresent) {
      // refresh => loadFromLocalStorage implies making call here
      if (!baseMetrics.value.length) {
        const baseFilters = { ...filters.value };
        baseFilters.sorts = baseFilters.sorts.filter((it: any) => it.type !== 'search');
        await additionalFetchProductsList(baseFilters);
      }
      const baseThemesMetrics = baseMetrics.value.find((it: any) => it.metricName === 'taxonomyThemes');
      if (baseThemesMetrics) {
        themes.value = baseThemesMetrics.value;
      }
      const baseTypesMetrics = baseMetrics.value.find((it: any) => it.metricName === 'taxonomyTypes');
      if (baseTypesMetrics) {
        types.value = [
          ...baseTypesMetrics.value.map((it: any) => {
            const matchingType = typesMetrics.value.find((x: any) => x.id === it.id);
            if (matchingType) {
              return { ...matchingType, isMatching: true, isSelected: false };
            }
            else {
              return { ...it, isMatching: false, isSelected: false };
            }
          }),
        ];
      }
    }
  }

  async function fetchMoreProducts(filters: Filters) {
    if (!apiKey.value) {
      throw new Error('No client token available');
    }
    const headers = new Headers();
    headers.set('Content-Type', 'application/json');
    headers.set('Accept', 'application/json');
    headers.set('Authorization', `Bearer ${apiKey.value}`);
    const requestFilters = { ...filters };
    requestFilters.cursor = nextPaginationKey.value;
    const body = JSON.stringify(requestFilters);
    try {
      const request = await fetch(`${apiURL.value}/products/list`, { method: 'POST', body, headers });
      const productDTOs = await request.json();
      products.value.push(...formatToProducts(productDTOs.data));
      nextPaginationKey.value = productDTOs.nextPage;
    }
    catch (err) {
      console.error('Unable to fetch paginated data', err);
    }
  }

  function formatToProducts(activities: Array<ProductDTO>) {
    const { currentLocale } = useLocale();
    const productsList: Array<Product> = [];
    activities?.forEach((activity) => {
      const { id, bookingUrls, currency, images, meetingPoint, names, priceFrom, originalPrice, sourceId, properties, importedInLangs } = activity;
      const name = names.find(it => it.lang === currentLocale.value)?.value ?? activity.name;
      const bookingUrl = bookingUrls.find(it => it.lang === currentLocale.value)?.value ?? activity.bookingUrl;
      const product = {
        id,
        name,
        bookingUrl,
        image: images?.[0]?.url ?? null,
        images,
        importedInLangs,
        location: meetingPoint?.location ?? null,
        meetingPoint: {
          description: meetingPoint.description ?? '',
          nearestPublicTransport: meetingPoint.nearestPublicTransport ?? '',
        },
        priceFrom,
        originalPrice,
        currency,
        properties,
        sourceId,
      };
      productsList.push(product);
    });
    return productsList;
  }

  return {
    woodyConfig,
    woodyTaxonomies,
    setupWoodyConfig,
    setApiKey,
    setupTaxonomies,
    fetchInitialProductsList,
    fetchProductsList,
    fetchMoreProducts,
    additionalFetchProductsList,
    getBaseMetrics,
    getProductsList,
    formatToProducts,
    apiURL,
    assetsURL,
    products,
    totalResults,
    nextPaginationKey,
    canonicals,
    themesRouting,
    priceRange,
    sessionDuration,
    timeRanges,
    themes,
    types,
    places,
    audiences,
    internals,
  };
}
